import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import type { Board } from '../../lib/api';
import { BoardCard, BoardRow, Grid } from './cards';
import type { View } from './ListingBar';
import { useInbox, useMembers, useOrg, useProjects } from './queries';
import { useRegisterBoards, useSelection } from './SelectionScope';

/** Board cards (or rows in the list view); inside a `SelectionScope` they join the page's selection (P16-4). */
export function BoardGrid({ boards, view = 'grid', subtitle }: { boards: readonly Board[]; view?: View; subtitle?: (b: Board) => string | undefined }) {
  const { t } = useTranslation();
  const key = useId();
  const scope = useSelection();
  const { org } = useOrg();
  const projects = useProjects(view === 'list' ? org?.id : undefined);
  const inbox = useInbox(view === 'list' ? org?.id : undefined);
  const members = useMembers(view === 'list' ? org?.id : undefined);
  useRegisterBoards(key, boards);
  const selection = (b: Board) => (scope ? { selected: scope.selection.ids.has(b.id), onSelect: (mods: Parameters<typeof scope.click>[1], toggle?: boolean) => scope.click(b.id, mods, toggle) } : undefined);

  if (view === 'list') {
    const projectName = new Map((projects.data ?? []).map((p) => [p.id, p.name]));
    if (inbox.data) projectName.set(inbox.data, t('dashboard.noProject'));
    const ownerName = new Map((members.data ?? []).map((m) => [m.userId, m.displayName]));
    const head = 'px-3 py-2 text-left font-sans text-xs font-medium text-fg-muted';
    return (
      <div className="overflow-x-auto rounded-lg bg-surface shadow-sm">
        <table className="w-full table-fixed border-collapse">
          <thead className="border-b border-border">
            <tr>
              <th className={`${head} w-10`} />
              <th className={`${head} w-1/2`}>{t('dashboard.listing.title')}</th>
              <th className={head}>{t('dashboard.project')}</th>
              <th className={head}>{t('dashboard.listing.edited')}</th>
              <th className={head}>{t('dashboard.listing.owner')}</th>
              <th className={`${head} w-12`} />
            </tr>
          </thead>
          <tbody>
            {boards.map((b) => (
              <BoardRow key={b.id} board={b} projectName={projectName.get(b.projectId)} ownerName={b.createdBy ? ownerName.get(b.createdBy) : undefined} selection={selection(b)} />
            ))}
          </tbody>
        </table>
      </div>
    );
  }
  return (
    <Grid>
      {boards.map((b) => (
        <BoardCard key={b.id} board={b} subtitle={subtitle?.(b)} selection={selection(b)} />
      ))}
    </Grid>
  );
}
